import React, { useState, useEffect } from 'react';
import {
  Layout, Typography, Button, Table, Space, Tag, Modal,
  notification, Card, Statistic, Row, Col, Input, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined, ReloadOutlined, EnvironmentOutlined,
  CheckCircleOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import AddDistrictForm from '../components/admin/district/AddDistrictForm';
import { fetchDistricts, createDistrict, updateDistrict, deleteDistrict } from '../api/api';

const { Content } = Layout;
const { Title, Text } = Typography;

const DistrictManagement = () => {
  const [districts, setDistricts] = useState([]);
  //// console.log(districts);
  const [loading, setLoading] = useState(true);
  const [editingDistrict, setEditingDistrict] = useState(null);
  // console.log(editingDistrict)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitHandler, setSubmitHandler] = useState(null);
  const [searchText, setSearchText] = useState('');

  const showNotification = (message, type = 'success') => {
    notification[type]({
      message: type === 'success' ? 'Success' : 'Error',
      description: message,
      placement: 'topRight',
      duration: 3,
    });
  };

  const loadDistricts = async () => {
    setLoading(true);
    try {
      const json = await fetchDistricts();
      if (json.success) {
        setDistricts(json.data);
      } else {
        showNotification('Failed to fetch districts', 'error');
      }
    } catch (error) {
      showNotification('Error fetching districts', 'error');
      console.error('Error fetching districts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDistricts();
  }, []);

  const openAddModal = () => {
    setEditingDistrict(null);
    setIsModalOpen(true);
  };

  const openEditModal = (district) => {
    //console.log('=== Opening Edit Modal ===');
    //console.log('Selected district:', district);
    //console.log('District ID:', district._id);
    //console.log('District Name:', district.districtName);
    //console.log('All fields:', Object.keys(district));

    // Make sure we're passing the full district object
    setEditingDistrict({ ...district }); // Create a new object to ensure state update
    setIsModalOpen(true);
  };

  const closeModal = () => {
    //console.log('=== Closing Modal ===');
    setIsModalOpen(false);
    // Clear editing district after animation
    setTimeout(() => {
      setEditingDistrict(null);
      setSubmitHandler(null);
    }, 300);
  };

  const handleSaveDistrict = async (formData) => {
    // console.log('=== handleSaveDistrict Called ===');
    try {
      let json;

      if (editingDistrict && editingDistrict._id) {
        // console.log('Updating district:', editingDistrict._id);
        // console.log('FormData entries:', Array.from(formData.entries()));
        
        // Always send as FormData (the backend will handle it properly)
        json = await updateDistrict(editingDistrict._id, formData);
      } else {
        // console.log('Creating new district');
        json = await createDistrict(formData);
      }

      if (json && json.success) {
        await loadDistricts();
        closeModal();
        showNotification(
          editingDistrict ? 'District updated successfully!' : 'District added successfully!',
          'success'
        );
      } else {
        console.error('Error response:', json);
        showNotification(json?.message || 'Failed to save district', 'error');
      }
    } catch (error) {
      console.error('Error in handleSaveDistrict:', error);
      showNotification(`Error saving district: ${error.message}`, 'error');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete District',
      content: `Are you sure you want to delete "${record.districtName}" district?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const json = await deleteDistrict(record._id);
          if (json.success) {
            await loadDistricts();
            showNotification('District deleted successfully!');
          } else {
            showNotification(json.message || 'Failed to delete district', 'error');
          }
        } catch (error) {
          showNotification('Error deleting district', 'error');
          console.error('Error deleting district:', error);
        }
      },
    });
  };

  // Enhanced table columns with better styling
  const columns = [
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>District Name</Text>,
      dataIndex: 'districtName',
      key: 'districtName',
      filteredValue: [searchText],
      onFilter: (value, record) =>
        record.districtName.toLowerCase().includes(value.toLowerCase()),
      render: (text, record) => (
        <Space size={12}>
          <div style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 16,
            fontWeight: 600,
            fontFamily: "'Rubik', sans-serif"
          }}>
            {text?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <Text strong style={{ fontSize: 15, color: '#141414', display: 'block', fontFamily: "'Rubik', sans-serif" }}>
              {text}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, fontFamily: "'Rubik', sans-serif" }}>
              <EnvironmentOutlined style={{ marginRight: 4, color: '#470172' }} />
              Industrial District
            </Text>
          </div>
        </Space>
      ),
      width: 280,
    },
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>Zone Classification</Text>,
      dataIndex: 'industrialZone',
      key: 'industrialZone',
      width: 180,
      render: (zone) => {
        const zoneConfig = {
          'A': { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'Premium' },
          'B': { color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', label: 'Advanced' },
          'C': { color: '#fa8c16', bg: '#fff7e6', border: '#ffd591', label: 'Developing' },
          'D': { color: '#f5222d', bg: '#fff1f0', border: '#ffa39e', label: 'Emerging' },
          'D+': { color: '#eb2f96', bg: '#fff0f6', border: '#ffadd2', label: 'Growth' },
        };
        const config = zoneConfig[zone] || zoneConfig['D+'];
        return (
          <div>
            <Tag
              style={{
                fontSize: 13,
                fontFamily: "'Rubik', sans-serif",
                fontWeight: 600,
                border: `1px solid ${config.border}`,
                background: config.bg,
                color: config.color,
                padding: '4px 14px',
                borderRadius: 6,
                marginBottom: 4
              }}
            >
              Zone {zone}
            </Tag>
            <div>
              <Text type="secondary" style={{ fontSize: 11, fontFamily: "'Rubik', sans-serif" }}>
                {config.label}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>MSME Enterprises</Text>,
      dataIndex: 'msmeCount',
      key: 'msmeCount',
      width: 160,
      render: (count) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 18, color: '#470172', fontFamily: "'Rubik', sans-serif" }}>
            {count ? parseInt(count).toLocaleString() : '0'}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, fontFamily: "'Rubik', sans-serif" }}>
            registered units
          </Text>
        </Space>
      ),
    },
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>Key Industries</Text>,
      dataIndex: 'majorIndustries',
      key: 'majorIndustries',
      width: 280,
      render: (text) => {
        if (!text) return <Text type="secondary" style={{ fontSize: 13 }}>No data</Text>;
        const industries = text.split(',').map(i => i.trim());
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {industries.slice(0, 2).map((industry, idx) => (
              <Tag
                key={idx}
                style={{
                  fontSize: 12,
                  fontFamily: "'Rubik', sans-serif",
                  background: '#f0f0f0',
                  border: '1px solid #d9d9d9',
                  color: '#595959',
                  borderRadius: 4,
                  padding: '2px 10px'
                }}
              >
                {industry}
              </Tag>
            ))}
            {industries.length > 2 && (
              <Tooltip title={industries.slice(2).join(', ')}>
                <Tag style={{
                  background: '#470172',
                  color: '#fff',
                  border: 'none',
                  fontSize: 12,
                  borderRadius: 4,
                  padding: '2px 10px',
                  cursor: 'pointer'
                }}>
                  +{industries.length - 2} more
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>Status</Text>,
      key: 'status',
      width: 120,
      render: () => (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 12px',
          background: '#f6ffed',
          border: '1px solid #b7eb8f',
          borderRadius: 6
        }}>
          <div style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#52c41a',
            boxShadow: '0 0 0 2px rgba(82, 196, 26, 0.2)'
          }} />
          <Text style={{ fontSize: 13, color: '#52c41a', fontWeight: 500, fontFamily: "'Rubik', sans-serif" }}>
            Active
          </Text>
        </div>
      ),
    },
    {
      title: <Text strong style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif" }}>Actions</Text>,
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size={8}>
          <Tooltip title="Edit District">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
              style={{
                color: '#470172',
                borderColor: '#d9d9d9',
                fontSize: 14,
                height: 32,
                fontFamily: "'Rubik', sans-serif"
              }}
            >
              Edit
            </Button>
          </Tooltip>
          <Tooltip title="Delete District">
            <Button
              type="default"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              style={{
                fontSize: 14,
                height: 32,
                fontFamily: "'Rubik', sans-serif"
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filteredDistricts = districts;

  return (
    <Layout style={{ minHeight: '100vh', background: '#f5f5f5', fontFamily: "'Rubik', sans-serif" }}>
      <Content style={{ padding: '32px 48px' }}>
        {/* Header */}
        <div style={{ marginBottom: 32 }}>
          <Title level={2} style={{
            margin: 0,
            color: '#470172',
            fontFamily: "'Rubik', sans-serif",
            fontSize: 32,
            fontWeight: 600
          }}>
            District Management
          </Title>
          <Text style={{ fontSize: 16, color: '#595959', fontFamily: "'Rubik', sans-serif" }}>
            Manage and monitor district profiles and industrial data
          </Text>
        </div>

        {/* Stats Cards */}
        <Row gutter={24} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <Statistic
                title={<Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959' }}>Total Districts</Text>}
                value={districts.length}
                valueStyle={{ color: '#470172', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}
                prefix={<EnvironmentOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <Statistic
                title={<Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959' }}>Active</Text>}
                value={districts.length}
                valueStyle={{ color: '#52c41a', fontSize: 32, fontFamily: "'Rubik', sans-serif", fontWeight: 600 }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card bordered={false} style={{ background: '#fff', borderRadius: 8 }}>
              <Statistic
                title={<Text style={{ fontSize: 15, fontFamily: "'Rubik', sans-serif", color: '#595959' }}>Last Updated</Text>}
                value="Today"
                valueStyle={{ color: '#1890ff', fontSize: 28, fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card
              bordered={false}
              style={{
                background: '#470172',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 120,
                cursor: 'pointer'
              }}
              onClick={openAddModal}
              hoverable
            >
              <Space direction="vertical" align="center" size={8}>
                <PlusOutlined style={{ fontSize: 32, color: '#fff' }} />
                <Text style={{ color: '#fff', fontSize: 16, fontFamily: "'Rubik', sans-serif", fontWeight: 500 }}>
                  Add New District
                </Text>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Enhanced Table Card */}
        <Card
          bordered={false}
          style={{
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
          bodyStyle={{ padding: 0 }}
        >
          {/* Enhanced Table Header */}
          <div style={{
            padding: '20px 24px',
            borderBottom: '2px solid #f0f0f0',
            background: '#fafafa'
          }}>
            <Row align="middle" justify="space-between">
              <Col>
                <Space size={12}>
                  <div style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: '#470172',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <EnvironmentOutlined style={{ fontSize: 18, color: '#fff' }} />
                  </div>
                  <div>
                    <Text strong style={{
                      fontSize: 18,
                      fontFamily: "'Rubik', sans-serif",
                      color: '#141414',
                      display: 'block'
                    }}>
                      District Registry
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13, fontFamily: "'Rubik', sans-serif" }}>
                      Comprehensive industrial district database
                    </Text>
                  </div>
                </Space>
              </Col>
              <Col>
                <Space size={12}>
                  <Input
                    placeholder="Search by district or industry..."
                    prefix={<SearchOutlined style={{ color: '#470172' }} />}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear
                    style={{
                      width: 320,
                      fontSize: 14,
                      fontFamily: "'Rubik', sans-serif",
                      borderRadius: 8
                    }}
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={loadDistricts}
                    loading={loading}
                    style={{
                      fontSize: 14,
                      fontFamily: "'Rubik', sans-serif",
                      height: 36,
                      borderRadius: 8
                    }}
                  >
                    Refresh
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openAddModal}
                    style={{
                      background: '#470172',
                      borderColor: '#470172',
                      fontSize: 14,
                      fontFamily: "'Rubik', sans-serif",
                      fontWeight: 500,
                      height: 36,
                      borderRadius: 8,
                      boxShadow: '0 2px 6px rgba(71, 1, 114, 0.3)'
                    }}
                  >
                    Add District
                  </Button>
                </Space>
              </Col>
            </Row>
          </div>

          {/* Enhanced Table */}
          <div style={{ padding: '16px 24px 24px' }}>
            <Table
              columns={columns}
              dataSource={filteredDistricts}
              rowKey="_id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '20', '50'],
                showTotal: (total, range) => (
                  <Text strong style={{
                    fontSize: 14,
                    fontFamily: "'Rubik', sans-serif",
                    color: '#470172'
                  }}>
                    Showing {range[0]}-{range[1]} of {total} districts
                  </Text>
                ),
              }}
              rowClassName={() => 'enhanced-table-row'}
              style={{
                fontSize: 14,
                fontFamily: "'Rubik', sans-serif"
              }}
            />
          </div>
        </Card>

        {/* Modal */}
        {isModalOpen && (
          <Modal
            open={isModalOpen}
            onCancel={closeModal}
            width="90%"
            style={{ top: 30, maxWidth: 1400 }}
            bodyStyle={{
              maxHeight: '80vh',
              overflowY: 'auto',
              padding: 0
            }}
            maskClosable={false}
            destroyOnClose={true} // Changed back to true to force re-render
            title={
              <Space>
                <div style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: editingDistrict ? '#fa8c16' : '#470172',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {editingDistrict ? (
                    <EditOutlined style={{ fontSize: 16, color: '#fff' }} />
                  ) : (
                    <PlusOutlined style={{ fontSize: 16, color: '#fff' }} />
                  )}
                </div>
                <div>
                  <Text strong style={{
                    fontSize: 20,
                    fontFamily: "'Rubik', sans-serif",
                    color: '#141414',
                    display: 'block'
                  }}>
                    {editingDistrict ? `Edit "${editingDistrict.districtName}" Details` : 'Add New District Profile'}
                  </Text>
                  {/* <Text type="secondary" style={{ fontSize: 13, fontFamily: "'Rubik', sans-serif" }}>
                    {editingDistrict ? `Editing district data` : 'Create a comprehensive district profile'}
                  </Text> */}
                </div>
              </Space>
            }
            footer={
              <Space>
                <Button
                  onClick={closeModal}
                  style={{
                    fontSize: 15,
                    fontFamily: "'Rubik', sans-serif"
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    if (submitHandler) {
                      submitHandler();
                    } else {
                      notification.error({
                        message: 'Error',
                        description: 'Submit handler not initialized. Please try again.'
                      });
                    }
                  }}
                  style={{
                    background: editingDistrict ? '#fa8c16' : '#470172',
                    borderColor: editingDistrict ? '#fa8c16' : '#470172',
                    fontSize: 15,
                    fontFamily: "'Rubik', sans-serif",
                    fontWeight: 500
                  }}
                >
                  {editingDistrict ? 'Update District' : 'Save District'}
                </Button>
              </Space>
            }
          >
            {/* Pass a stable key and ensure editingDistrict is cloned */}
            <AddDistrictForm
              key={editingDistrict ? `edit-${editingDistrict._id}` : 'new'}
              onSave={handleSaveDistrict}
              onCancel={closeModal}
              editingDistrict={editingDistrict ? { ...editingDistrict } : null}
              setSubmitHandler={setSubmitHandler}
            />
          </Modal>
        )}
      </Content>

      {/* Enhanced Table Styling */}
      <style>{`
        .enhanced-table-row {
          transition: all 0.2s ease;
        }
        .enhanced-table-row:hover {
          background: #fafafa !important;
          box-shadow: 0 2px 8px rgba(71, 1, 114, 0.08);
          transform: translateY(-1px);
        }
        .ant-table-thead > tr > th {
          background: #fafafa !important;
          font-weight: 600 !important;
          border-bottom: 2px solid #470172 !important;
          padding: 16px !important;
        }
        .ant-table-tbody > tr > td {
          padding: 16px !important;
        }
        .ant-pagination-item-active {
          border-color: #470172 !important;
          background: #470172 !important;
        }
        .ant-pagination-item-active a {
          color: #fff !important;
        }
        .ant-pagination-item:hover {
          border-color: #470172 !important;
        }
        .ant-pagination-item:hover a {
          color: #470172 !important;
        }
      `}</style>
    </Layout>
  );
};

export default DistrictManagement;
